import React, { useState } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonMenuButton,
  IonIcon,
  IonSkeletonText,
  IonRefresher,
  IonRefresherContent,
  IonFab,
  IonFabButton,
  useIonRouter,
  useIonToast,
} from '@ionic/react';
import {
  search,
  close,
  people,
  home,
  map,
  mapOutline,
  add,
  createOutline,
  trashOutline,
  listOutline,
  gridOutline,
  pencilOutline,
  addCircleOutline,
  downloadOutline,
} from 'ionicons/icons';
import { exportAllSectors } from '@/api/sectors';
import { useSectors, useCreateSector, useUpdateSector, useDeleteSector } from '@/hooks/useSectors';
import { usePermission } from '@/hooks/usePermission';
import type { Sector, SectorFormData } from '@/types/sector';
import ConfirmDialog from '@/components/shared/ConfirmDialog';

const SECTOR_COLORS = [
  '#4f46e5', '#7c3aed', '#059669', '#d97706',
  '#dc2626', '#0891b2', '#be185d', '#65a30d',
];

const EMPTY_FORM: SectorFormData = { name: '', number: 1, color: '#4f46e5' };

type ViewMode = 'list' | 'grid';
type SortKey = 'number' | 'name' | 'member_count' | 'family_count';

const SectorListPage: React.FC = () => {
  const { data: sectors, isLoading, refetch } = useSectors();
  const router = useIonRouter();
  const [presentToast] = useIonToast();
  const { can } = usePermission();
  const canCreate = can('sectors.create');
  const canEdit = can('sectors.edit');
  const canDelete = can('sectors.delete');

  // View & sort
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [sortKey, setSortKey] = useState<SortKey>('number');
  const [sortAsc, setSortAsc] = useState(true);
  const [showSearch, setShowSearch] = useState(false);
  const [searchText, setSearchText] = useState('');

  // Export-all state
  const [exportingAll, setExportingAll] = useState<'xlsx' | 'pdf' | null>(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingSector, setEditingSector] = useState<Sector | null>(null);
  const [formData, setFormData] = useState<SectorFormData>(EMPTY_FORM);
  const [deleteSectorTarget, setDeleteSectorTarget] = useState<Sector | null>(null);

  const createMut = useCreateSector();
  const updateMut = useUpdateSector();
  const deleteMut = useDeleteSector();
  const isSaving = createMut.isPending || updateMut.isPending;

  const handleRefresh = async (event: CustomEvent) => {
    await refetch();
    event.detail.complete();
  };

  const handleExportAll = async (format: 'xlsx' | 'pdf') => {
    setExportingAll(format);
    try {
      await exportAllSectors({ format });
      void presentToast({
        message: format === 'pdf' ? 'Export PDF descărcat.' : 'Export Excel descărcat.',
        duration: 2000, color: 'success', position: 'bottom',
      });
    } catch {
      void presentToast({ message: 'Eroare la export.', duration: 2000, color: 'danger', position: 'bottom' });
    } finally {
      setExportingAll(null);
    }
  };

  // Computed
  const totalFamilies = sectors?.reduce((sum, s) => sum + s.family_count, 0) ?? 0;
  const totalMembers = sectors?.reduce((sum, s) => sum + s.member_count, 0) ?? 0;

  const filteredSectors = (sectors ?? [])
    .filter((s) => !searchText || s.name.toLowerCase().includes(searchText.toLowerCase()))
    .sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'number') cmp = a.number - b.number;
      else if (sortKey === 'name') cmp = a.name.localeCompare(b.name);
      else if (sortKey === 'member_count') cmp = a.member_count - b.member_count;
      else if (sortKey === 'family_count') cmp = a.family_count - b.family_count;
      return sortAsc ? cmp : -cmp;
    });

  const openCreate = () => {
    const nextNumber = sectors ? Math.max(0, ...sectors.map((s) => s.number)) + 1 : 1;
    setEditingSector(null);
    setFormData({ name: '', number: nextNumber, color: SECTOR_COLORS[(nextNumber - 1) % SECTOR_COLORS.length]! });
    setShowModal(true);
  };

  const openEdit = (sector: Sector, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingSector(sector);
    setFormData({ name: sector.name, number: sector.number, color: sector.color || '#4f46e5' });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      void presentToast({ message: 'Numele sectorului este obligatoriu.', duration: 2000, color: 'warning', position: 'bottom' });
      return;
    }
    try {
      if (editingSector) {
        await updateMut.mutateAsync({ id: editingSector.id, data: formData });
        void presentToast({ message: 'Sector actualizat.', duration: 2000, color: 'success', position: 'bottom' });
      } else {
        await createMut.mutateAsync(formData);
        void presentToast({ message: 'Sector creat.', duration: 2000, color: 'success', position: 'bottom' });
      }
      setShowModal(false);
    } catch {
      void presentToast({ message: 'Eroare la salvare.', duration: 2000, color: 'danger', position: 'bottom' });
    }
  };

  const handleDelete = async () => {
    if (!deleteSectorTarget) return;
    try {
      await deleteMut.mutateAsync(deleteSectorTarget.id);
      void presentToast({ message: 'Sector șters.', duration: 2000, color: 'success', position: 'bottom' });
    } catch {
      void presentToast({ message: 'Eroare la ștergere.', duration: 2000, color: 'danger', position: 'bottom' });
    }
    setDeleteSectorTarget(null);
  };

  const navigateToSector = (id: string) => {
    router.push(`/sectors/${id}`);
  };

  const getSectorColor = (sector: Sector, idx: number) =>
    sector.color || SECTOR_COLORS[idx % SECTOR_COLORS.length]!;

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar className="crm-toolbar">
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Sectoare</IonTitle>
          <IonButtons slot="end">
            <button
              className="crm-toolbar-icon"
              onClick={() => { setShowSearch(!showSearch); if (showSearch) setSearchText(''); }}
            >
              <IonIcon icon={showSearch ? close : search} />
            </button>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        {showSearch && (
          <div style={{ padding: '12px 20px 0', maxWidth: 1200, margin: '0 auto' }}>
            <input
              type="text"
              placeholder="Caută sectoare..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              autoFocus
              style={{
                width: '100%', padding: '10px 0', fontSize: 14, fontWeight: 700,
                fontFamily: "'Lato', sans-serif", color: '#1c1d21',
                border: 'none', borderBottom: '1px solid #ececf2', outline: 'none',
                background: 'transparent',
              }}
              onFocus={(e) => { e.target.style.borderBottomColor = '#5e81f4'; }}
              onBlur={(e) => { e.target.style.borderBottomColor = '#ececf2'; }}
            />
          </div>
        )}

        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '16px 20px' }}>

          {/* ── Compact stats row ── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
            <div className="crm-stat-card crm-animate-in">
              <div className="crm-stat-card__label">
                <span>Sectoare</span>
                <div className="crm-stat-card__icon" style={{ backgroundColor: '#7c3aed20', color: '#7c3aed' }}>
                  <IonIcon icon={map} />
                </div>
              </div>
              <div className="crm-stat-card__value">{sectors?.length ?? 0}</div>
            </div>
            <div className="crm-stat-card crm-animate-in">
              <div className="crm-stat-card__label">
                <span>Familii</span>
                <div className="crm-stat-card__icon" style={{ backgroundColor: '#4f46e520', color: '#4f46e5' }}>
                  <IonIcon icon={home} />
                </div>
              </div>
              <div className="crm-stat-card__value">{totalFamilies}</div>
            </div>
            <div className="crm-stat-card crm-animate-in">
              <div className="crm-stat-card__label">
                <span>Membri</span>
                <div className="crm-stat-card__icon" style={{ backgroundColor: '#05966920', color: '#059669' }}>
                  <IonIcon icon={people} />
                </div>
              </div>
              <div className="crm-stat-card__value">{totalMembers}</div>
            </div>
          </div>

          {/* ── Controls bar ── */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div className="crm-view-toggle">
              <button
                className={`crm-view-toggle__btn ${viewMode === 'list' ? 'crm-view-toggle__btn--active' : ''}`}
                onClick={() => setViewMode('list')}
              >
                <IonIcon icon={listOutline} style={{ fontSize: 14 }} /> Listă
              </button>
              <button
                className={`crm-view-toggle__btn ${viewMode === 'grid' ? 'crm-view-toggle__btn--active' : ''}`}
                onClick={() => setViewMode('grid')}
              >
                <IonIcon icon={gridOutline} style={{ fontSize: 14 }} /> Grilă
              </button>
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <button
                className="form-btn form-btn--secondary"
                style={{ fontSize: 12, padding: '4px 10px', height: 32 }}
                onClick={() => handleExportAll('xlsx')}
                disabled={exportingAll !== null}
                title="Descarcă toate sectoarele (Excel)"
              >
                <IonIcon icon={downloadOutline} style={{ fontSize: 13, marginRight: 3 }} />
                {exportingAll === 'xlsx' ? 'Se descarcă...' : 'Excel'}
              </button>
              <button
                className="form-btn form-btn--secondary"
                style={{ fontSize: 12, padding: '4px 10px', height: 32 }}
                onClick={() => handleExportAll('pdf')}
                disabled={exportingAll !== null}
                title="Descarcă toate sectoarele (PDF)"
              >
                <IonIcon icon={downloadOutline} style={{ fontSize: 13, marginRight: 3 }} />
                {exportingAll === 'pdf' ? 'Se descarcă...' : 'PDF'}
              </button>
              <select
                className="form-input form-select"
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value as SortKey)}
                style={{ height: 32, fontSize: 12, fontWeight: 600, padding: '0 28px 0 8px', minWidth: 0, width: 'auto', textTransform: 'uppercase', letterSpacing: 0.5 }}
              >
                <option value="number">Nr.</option>
                <option value="name">Nume</option>
                <option value="family_count">Familii</option>
                <option value="member_count">Membri</option>
              </select>
              <button className="crm-sort-button" onClick={() => setSortAsc(!sortAsc)} style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                {sortAsc ? '↑' : '↓'}
              </button>
            </div>
          </div>

          {/* ── Content ── */}
          {isLoading ? (
            viewMode === 'list' ? (
              <div>
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} style={{ padding: '16px 0', borderBottom: '1px solid #f1f5f9' }}>
                    <IonSkeletonText animated style={{ width: '40%', height: 16 }} />
                    <IonSkeletonText animated style={{ width: '20%', height: 12, marginTop: 8 }} />
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 14 }}>
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="crm-card" style={{ padding: 20 }}>
                    <IonSkeletonText animated style={{ width: '50%', height: 18 }} />
                    <IonSkeletonText animated style={{ width: '30%', height: 14, marginTop: 12 }} />
                    <IonSkeletonText animated style={{ width: '80%', height: 6, marginTop: 16 }} />
                  </div>
                ))}
              </div>
            )
          ) : viewMode === 'list' ? (
            /* ══════════════ LIST VIEW ══════════════ */
            <div className="crm-card" style={{ overflow: 'auto' }}>
              <table className="crm-table">
                <thead>
                  <tr>
                    <th style={{ minWidth: 240 }}>Sector</th>
                    <th style={{ width: 100 }}>Familii</th>
                    <th style={{ width: 100 }}>Membri</th>
                    <th style={{ width: 180 }}>Vizite luna aceasta</th>
                    <th style={{ width: 160 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSectors.map((sector, idx) => {
                    const color = getSectorColor(sector, idx);
                    return (
                      <tr
                        key={sector.id}
                        className="crm-animate-in"
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigateToSector(sector.id)}
                      >
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div
                              style={{
                                width: 36, height: 36, borderRadius: 10,
                                backgroundColor: `${color}15`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0,
                              }}
                            >
                              <span style={{ fontSize: 14, fontWeight: 800, color }}>{sector.number}</span>
                            </div>
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span className="crm-table__name">{sector.name}</span>
                                <span
                                  className={`sector-geo-badge ${sector.has_geometry ? 'sector-geo-badge--defined' : 'sector-geo-badge--undefined'}`}
                                  onClick={(e) => { e.stopPropagation(); router.push(`/sectors/${sector.id}/boundary`); }}
                                  title={sector.has_geometry ? 'Editeaza zona pe harta' : 'Defineste zona pe harta'}
                                >
                                  <IonIcon icon={sector.has_geometry ? map : mapOutline} />
                                  {sector.has_geometry ? 'Zona' : 'Fara zona'}
                                </span>
                              </div>
                              <div className="crm-table__sub">Sector {sector.number}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div style={{ fontSize: 15, fontWeight: 700 }}>{sector.family_count}</div>
                        </td>
                        <td>
                          <div style={{ fontSize: 15, fontWeight: 700 }}>{sector.member_count}</div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ flex: 1, height: 6, borderRadius: 3, backgroundColor: '#e2e8f0', overflow: 'hidden' }}>
                              <div style={{ height: '100%', borderRadius: 3, width: `${sector.visited_this_month_pct}%`, backgroundColor: color, transition: 'width 0.5s ease' }} />
                            </div>
                            <span style={{ fontSize: 13, fontWeight: 700, color: '#475569', minWidth: 36, textAlign: 'right' }}>
                              {sector.visited_this_month_pct}%
                            </span>
                          </div>
                        </td>
                        <td>
                          {(canEdit || canDelete) && (
                            <div className="crm-table__actions">
                              {canEdit && (
                                <button
                                  className="sector-zone-action"
                                  onClick={(e) => { e.stopPropagation(); router.push(`/sectors/${sector.id}/boundary`); }}
                                  title={sector.has_geometry ? 'Editeaza zona' : 'Deseneaza zona'}
                                >
                                  <IonIcon icon={sector.has_geometry ? pencilOutline : addCircleOutline} />
                                  {sector.has_geometry ? 'Editeaza zona' : 'Deseneaza'}
                                </button>
                              )}
                              {canEdit && (
                                <button
                                  className="crm-sort-button"
                                  onClick={(e) => openEdit(sector, e)}
                                  title="Editează detalii"
                                >
                                  <IonIcon icon={createOutline} style={{ fontSize: 16 }} />
                                </button>
                              )}
                              {canDelete && (
                                <button
                                  className="crm-sort-button"
                                  onClick={(e) => { e.stopPropagation(); setDeleteSectorTarget(sector); }}
                                  title="Șterge"
                                  style={{ color: '#ef4444' }}
                                >
                                  <IonIcon icon={trashOutline} style={{ fontSize: 16 }} />
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            /* ══════════════ GRID VIEW ══════════════ */
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 14 }}>
              {filteredSectors.map((sector, idx) => {
                const color = getSectorColor(sector, idx);
                const visitedPct = sector.visited_this_month_pct ?? 0;
                return (
                  <div
                    key={sector.id}
                    className="crm-card crm-card--hover crm-animate-in"
                    style={{ cursor: 'pointer', padding: 0, overflow: 'hidden', position: 'relative' }}
                    onClick={() => navigateToSector(sector.id)}
                  >
                    {/* Top color bar */}
                    <div style={{ height: 4, backgroundColor: color }} />

                    <div style={{ padding: '20px 20px 0' }}>
                      {/* Number circle + Name */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
                        <div
                          style={{
                            width: 44, height: 44, borderRadius: 12,
                            backgroundColor: `${color}18`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <span style={{ fontSize: 16, fontWeight: 800, color }}>{sector.number}</span>
                        </div>
                        <div>
                          <span style={{ fontWeight: 700, fontSize: 15, color: '#1c1d21' }}>{sector.name}</span>
                          <div style={{ fontSize: 12, color: '#8181a5', marginTop: 2 }}>Sector {sector.number}</div>
                        </div>
                      </div>
                    </div>

                    {/* Progress bar section */}
                    <div style={{ padding: '14px 20px 0' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                        <span style={{ fontSize: 12, color: '#8181a5', fontWeight: 500 }}>Vizite luna aceasta</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#475569' }}>{visitedPct}%</span>
                      </div>
                      <div style={{ height: 6, borderRadius: 3, backgroundColor: '#e2e8f0', overflow: 'hidden' }}>
                        <div style={{ height: '100%', borderRadius: 3, width: `${visitedPct}%`, backgroundColor: color, transition: 'width 0.5s ease' }} />
                      </div>
                    </div>

                    {/* Bottom stats + action row */}
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '14px 20px 0', marginTop: 14,
                      borderTop: '1px solid #f0f0f3',
                    }}>
                      <div style={{ display: 'flex', gap: 16 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <IonIcon icon={home} style={{ fontSize: 13, color: '#8181a5' }} />
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>{sector.family_count}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <IonIcon icon={people} style={{ fontSize: 13, color: '#8181a5' }} />
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>{sector.member_count}</span>
                        </div>
                      </div>
                      {(canEdit || canDelete) && (
                        <div style={{ display: 'flex', gap: 4 }}>
                          {canEdit && (
                            <button
                              className="crm-icon-btn"
                              onClick={(e) => openEdit(sector, e)}
                            >
                              <IonIcon icon={createOutline} style={{ fontSize: 15, color: '#64748b' }} />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              className="crm-icon-btn crm-icon-btn--danger"
                              onClick={(e) => { e.stopPropagation(); setDeleteSectorTarget(sector); }}
                            >
                              <IonIcon icon={trashOutline} style={{ fontSize: 15, color: '#ef4444' }} />
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Zone status strip */}
                    {canEdit && (
                      <div className={`sector-card-zone-strip${sector.has_geometry ? '' : ' sector-card-zone-strip--empty'}`}>
                        <span style={{ color: sector.has_geometry ? '#059669' : '#b45309', display: 'flex', alignItems: 'center', gap: 4 }}>
                          <IonIcon icon={sector.has_geometry ? map : mapOutline} style={{ fontSize: 13 }} />
                          {sector.has_geometry ? 'Zona definita' : 'Zona nedefinita'}
                        </span>
                        <button
                          className={`sector-card-zone-strip__link${sector.has_geometry ? '' : ' sector-card-zone-strip__link--cta'}`}
                          onClick={(e) => { e.stopPropagation(); router.push(`/sectors/${sector.id}/boundary`); }}
                        >
                          <IonIcon icon={sector.has_geometry ? pencilOutline : addCircleOutline} style={{ fontSize: 12 }} />
                          {sector.has_geometry ? 'Editeaza zona' : 'Deseneaza zona'}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {!isLoading && filteredSectors.length === 0 && (
            <div className="crm-empty-state">
              <div className="crm-empty-state__icon"><IonIcon icon={map} /></div>
              <h3 className="crm-empty-state__title">Niciun sector</h3>
              <p className="crm-empty-state__text">
                {searchText ? 'Niciun sector nu corespunde căutării.' : 'Nu au fost configurate sectoare.'}
              </p>
            </div>
          )}
        </div>

        {/* FAB create button */}
        {canCreate && (
          <IonFab vertical="bottom" horizontal="end" slot="fixed" style={{ marginBottom: 16, marginRight: 16 }}>
            <IonFabButton onClick={openCreate}>
              <IonIcon icon={add} />
            </IonFabButton>
          </IonFab>
        )}

        {/* ── Create/Edit Sector Modal ── */}
        {showModal && (
          <div className="logout-overlay" onClick={() => { if (!isSaving) setShowModal(false); }}>
            <div className="logout-dialog" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440, width: '90%', textAlign: 'left' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                  {editingSector ? 'Editare Sector' : 'Sector Nou'}
                </h3>
                <button onClick={() => setShowModal(false)} disabled={isSaving} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                  <IonIcon icon={close} style={{ fontSize: 22, color: '#94a3b8' }} />
                </button>
              </div>

              <div className="form-field" style={{ marginBottom: 16 }}>
                <label className="form-field__label">Nume sector *</label>
                <input
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="ex: Sector Centru"
                  autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter') { void handleSave(); } }}
                />
              </div>

              <div className="form-field" style={{ marginBottom: 16 }}>
                <label className="form-field__label">Număr sector *</label>
                <input
                  className="form-input"
                  type="number"
                  min={1}
                  value={formData.number}
                  onChange={(e) => setFormData({ ...formData, number: parseInt(e.target.value) || 1 })}
                />
              </div>

              <div className="form-field" style={{ marginBottom: 24 }}>
                <label className="form-field__label">Culoare</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {SECTOR_COLORS.map((c) => (
                    <button
                      key={c}
                      onClick={() => setFormData({ ...formData, color: c })}
                      style={{
                        width: 32, height: 32, borderRadius: 8, backgroundColor: c,
                        border: formData.color === c ? '3px solid #0f172a' : '3px solid transparent',
                        cursor: 'pointer', transition: 'border 0.15s',
                      }}
                    />
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button className="form-btn form-btn--secondary" onClick={() => setShowModal(false)} disabled={isSaving}>
                  Anulează
                </button>
                <button className="form-btn form-btn--primary" onClick={handleSave} disabled={isSaving}>
                  {isSaving && <span className="login-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />}
                  {isSaving ? 'Se salvează...' : editingSector ? 'Salvează' : 'Creează'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Delete Confirmation ── */}
        <ConfirmDialog
          isOpen={!!deleteSectorTarget}
          title="Ștergere Sector"
          message={`Sigur doriți să ștergeți sectorul „${deleteSectorTarget?.name}"? Familiile și membrii vor fi dezasociați, nu șterși.`}
          confirmLabel="Șterge"
          variant="danger"
          isLoading={deleteMut.isPending}
          onConfirm={handleDelete}
          onCancel={() => setDeleteSectorTarget(null)}
        />
      </IonContent>
    </IonPage>
  );
};

export default SectorListPage;
